import { effect } from '@preact/signals';
import { render } from 'preact';
import { App } from './app';
import { readBootConfig } from './core/boot-config';
import { fetchWidgetConfig } from './core/config';
import { flushPendingSave, loadStorage, scheduleSave } from './core/storage';
import { init as initTelemetry } from './core/telemetry';
import { applyTheme } from './core/theme';
import { hasUserLocale, isLocale, setLocale } from './i18n';
import {
  consentGiven,
  runtimeConfig,
  sessionClosed,
  userName,
} from './state/widget';
import stylesCSS from './styles/index.css?inline';

const HOST_ID = 'cp-widget-host';
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';

function ensureInterFont(): void {
  if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
  const pre1 = document.createElement('link');
  pre1.rel = 'preconnect';
  pre1.href = 'https://fonts.googleapis.com';
  const pre2 = document.createElement('link');
  pre2.rel = 'preconnect';
  pre2.href = 'https://fonts.gstatic.com';
  pre2.crossOrigin = '';
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.append(pre1, pre2, link);
}

function mount(): void {
  if (document.getElementById(HOST_ID)) return;
  ensureInterFont();

  const host = document.createElement('div');
  host.id = HOST_ID;
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = stylesCSS;
  shadow.appendChild(styleEl);

  const root = document.createElement('div');
  shadow.appendChild(root);

  const boot = readBootConfig();
  if (boot.token) hydratePersistedState(boot.token);

  if (boot.token) {
    const storage = loadStorage(boot.token);
    initTelemetry({
      apiUrl: boot.apiUrl,
      token: boot.token,
      clientId: storage.clientId,
    });

    // Dev-stub: partner-hardcoded runtime через window.__CPW_CONFIG__.runtime.
    // Применяется синхронно до render, чтобы виджет сразу открылся с правильной темой.
    const devRuntime = (window as typeof window & {
      __CPW_CONFIG__?: { runtime?: typeof runtimeConfig.value };
    }).__CPW_CONFIG__?.runtime;
    if (devRuntime) {
      runtimeConfig.value = devRuntime;
      applyTheme(host, devRuntime.colors);
      if (devRuntime.locale && !hasUserLocale() && isLocale(devRuntime.locale)) {
        setLocale(devRuntime.locale, false);
      }
    }

    // HTTP fetch: асинхронно обновит signals и тему, когда сервер ответит.
    // До этого момента виджет рендерится с :host defaults — flash на ~100-300 мс
    // незаметен, т.к. FAB закрыт.
    fetchWidgetConfig(boot.apiUrl, boot.token).then((rc) => {
      if (!rc) return;
      runtimeConfig.value = rc;
      applyTheme(host, rc.colors);
      if (rc.locale && !hasUserLocale() && isLocale(rc.locale)) {
        setLocale(rc.locale, false);
      }
    });
  }

  render(<App config={boot} />, root);
}

function hydratePersistedState(token: string): void {
  const initial = loadStorage(token);

  // Hydrate signals. История сообщений НЕ восстанавливается из LS —
  // она приходит на каждый WS connect через credentials-set от backend'а.
  sessionClosed.value = initial.session.closed;
  consentGiven.value = initial.identity.consent;
  userName.value = initial.identity.name ?? '';

  // Подписка: при любом signal change — пишем свежий snapshot через debounced save.
  // Читаем current из storage, чтобы не затирать clientId и partnerMeta (когда
  // они появятся в Phase 2).
  effect(() => {
    const current = loadStorage(token);
    scheduleSave(token, {
      ...current,
      identity: {
        ...current.identity,
        consent: consentGiven.value,
        name: userName.value || undefined,
      },
      session: {
        ...current.session,
        closed: sessionClosed.value,
      },
    });
  });

  window.addEventListener('beforeunload', () => {
    flushPendingSave();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}
